import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import { useDiscussionRepository } from '../../../../../../services/adaptors/discussionRepositoryAdapter'
import { Discussion } from '../../../../../../domain/entity/DiscussionEntity'
import { VersionReportVersionEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { intl } from '../../../../../../i18n'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../store/messages'

type UseDiscussionProps = {
  versionPageUuid?: string
  currentVersionUuid?: string
  templatePageUuid?: string
  versionList: VersionReportVersionEntity[]
}

export const useDiscussion = ({
  versionPageUuid,
  currentVersionUuid,
  templatePageUuid,
  versionList,
}: UseDiscussionProps) => {
  const [currentDiscussion, setCurrentDiscussion] = useState<Discussion | null>(
    null
  )
  const [versionCache, setVersionCache] = useState<Map<string, Discussion>>(
    new Map()
  )
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false)
  const [loadingVersionUuids, setLoadingVersionUuids] = useState<Set<string>>(
    new Set()
  )
  const versionCacheRef = useRef(versionCache)
  const loadingVersionUuidsRef = useRef(loadingVersionUuids)
  versionCacheRef.current = versionCache
  loadingVersionUuidsRef.current = loadingVersionUuids
  const { getDiscussion, getDiscussionByVersion } = useDiscussionRepository()
  const dispatch = useDispatch()

  const pastVersions = useMemo(() => {
    if (!currentVersionUuid) return []
    const currentVer = versionList.find(v => v.uuid === currentVersionUuid)
    if (!currentVer) return []

    const currentCreatedAt = moment(currentVer.createdAt).valueOf()
    return [...versionList]
      .filter(
        v =>
          v.uuid !== currentVersionUuid &&
          moment(v.createdAt).valueOf() < currentCreatedAt
      )
      .sort(
        (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
      )
  }, [versionList, currentVersionUuid])

  const fetchCurrent = useCallback(async () => {
    if (!versionPageUuid) {
      setCurrentDiscussion(null)
      return
    }
    try {
      setIsLoadingCurrent(true)
      const result = await getDiscussion(versionPageUuid)
      setCurrentDiscussion(result)
    } catch {
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: intl.formatMessage({ id: 'discussion.error.fetch' }),
        })
      )
      setCurrentDiscussion(null)
    } finally {
      setIsLoadingCurrent(false)
    }
  }, [versionPageUuid, getDiscussion, dispatch])

  useEffect(() => {
    fetchCurrent()
  }, [fetchCurrent])

  useEffect(() => {
    setVersionCache(new Map())
  }, [versionPageUuid])

  const loadVersion = useCallback(
    async (versionUuid: string) => {
      if (!templatePageUuid) return
      if (loadingVersionUuidsRef.current.has(versionUuid)) return
      if (versionCacheRef.current.has(versionUuid)) return
      setLoadingVersionUuids(prev => {
        const next = new Set(prev)
        next.add(versionUuid)
        return next
      })
      try {
        const result = await getDiscussionByVersion(
          versionUuid,
          templatePageUuid
        )
        if (result) {
          setVersionCache(prev => {
            const next = new Map(prev)
            next.set(versionUuid, result.discussion)
            return next
          })
        }
      } catch {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'discussion.error.fetch' }),
          })
        )
      } finally {
        setLoadingVersionUuids(prev => {
          const next = new Set(prev)
          next.delete(versionUuid)
          return next
        })
      }
    },
    [templatePageUuid, getDiscussionByVersion, dispatch]
  )

  return {
    currentDiscussion,
    pastVersions,
    versionCache,
    isLoadingCurrent,
    loadingVersionUuids,
    loadVersion,
    refreshCurrent: fetchCurrent,
  }
}
